import { useState, useRef, useCallback } from 'react';
import { blobToBase64, base64ToBlobUrl } from '@/lib/utils';

export interface RecorderState {
  isRecording: boolean;
  isPaused: boolean;
  recordingTime: number;
  audioURL: string | null;
  audioBlob: Blob | null;
  audioBase64: string | null;
  error: string | null;
}

export interface RecorderControls {
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  playRecording: () => void;
  deleteRecording: () => void;
  loadFromBase64: (base64: string) => void;
}

export function useVoiceRecorder() {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);

  const [state, setState] = useState<RecorderState>({
    isRecording: false,
    isPaused: false,
    recordingTime: 0,
    audioURL: null,
    audioBlob: null,
    audioBase64: null,
    error: null,
  });

  const startRecording = useCallback(async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('MediaRecorder API not supported in this browser');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm' 
        : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : 'audio/wav';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        const audioURL = URL.createObjectURL(audioBlob);
        
        try {
          const audioBase64 = await blobToBase64(audioBlob);
          
          setState(prev => ({
            ...prev,
            isRecording: false,
            isPaused: false,
            audioURL,
            audioBlob,
            audioBase64,
          }));
        } catch (error) {
          console.error('Error converting blob to base64:', error);
          setState(prev => ({
            ...prev,
            isRecording: false,
            isPaused: false,
            audioURL,
            audioBlob,
            audioBase64: null,
            error: 'Failed to convert recording to base64',
          }));
        }

        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }

        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      };

      mediaRecorder.start(100);

      setState(prev => ({
        ...prev,
        isRecording: true,
        isPaused: false,
        recordingTime: 0,
        audioURL: null,
        audioBlob: null,
        audioBase64: null,
        error: null,
      }));

      timerRef.current = setInterval(() => {
        setState(prev => ({
          ...prev,
          recordingTime: prev.recordingTime + 1,
        }));
      }, 1000);
    } catch (error) {
      console.error('Error starting recording:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to start recording',
      }));
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && state.isRecording) {
      mediaRecorderRef.current.stop();
    }
  }, [state.isRecording]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && state.isRecording && !state.isPaused) {
      mediaRecorderRef.current.pause();
      setState(prev => ({
        ...prev,
        isPaused: true,
      }));

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [state.isRecording, state.isPaused]);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && state.isRecording && state.isPaused) {
      mediaRecorderRef.current.resume();
      setState(prev => ({
        ...prev,
        isPaused: false,
      }));

      timerRef.current = setInterval(() => {
        setState(prev => ({
          ...prev,
          recordingTime: prev.recordingTime + 1,
        }));
      }, 1000);
    }
  }, [state.isRecording, state.isPaused]);

  const playRecording = useCallback(() => {
    if (state.audioURL) {
      if (!audioPlayerRef.current) {
        audioPlayerRef.current = new Audio(state.audioURL);
      } else {
        audioPlayerRef.current.src = state.audioURL;
      }
      audioPlayerRef.current.play();
    }
  }, [state.audioURL]);

  const deleteRecording = useCallback(() => {
    if (state.audioURL) {
      URL.revokeObjectURL(state.audioURL);
    }

    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current.src = '';
    }

    setState(prev => ({
      ...prev,
      audioURL: null,
      audioBlob: null,
      audioBase64: null,
      recordingTime: 0,
    }));
  }, [state.audioURL]);

  const loadFromBase64 = useCallback((base64: string) => {
    try {
      const audioURL = base64ToBlobUrl(base64);
      setState(prev => ({
        ...prev,
        audioURL,
        audioBase64: base64,
        audioBlob: null,
      }));
    } catch (error) {
      console.error('Error loading base64 audio:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to load audio from base64',
      }));
    }
  }, []);

  const controls: RecorderControls = {
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    playRecording,
    deleteRecording,
    loadFromBase64,
  };

  return { state, controls };
}
